import { useQuery } from '@tanstack/react-query'
import { supabaseApi } from '~/supabase/api'
import { Constants, type Enums } from '~/supabase/schema'

export type AppRole = Enums<'app_role'>

export const USER_ROLE_QUERY_KEY = 'user-role'

export const isAppRole = (value: unknown): value is AppRole =>
  Constants.public.Enums.app_role.some((role) => role === value)

/**
 * Reads the current user's role from the `user_role` JWT claim that is injected
 * by the `custom_access_token_hook` Postgres auth hook. Refreshed on login/logout
 * by invalidating USER_ROLE_QUERY_KEY in AuthProvider's onAuthStateChange.
 */
export const useRole = () => {
  const {
    data: role = null,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [USER_ROLE_QUERY_KEY] as const,
    queryFn: async (): Promise<AppRole | null> => {
      const { data, error } = await supabaseApi.auth.getClaims()
      if (error) throw error
      const claim: unknown = data?.claims.user_role
      return isAppRole(claim) ? claim : 'admin'
    },
    staleTime: 1000 * 60 * 60 * 60,
  })

  return { role, isLoading, isError, refetch }
}

/** No `allow` list means the route is open to every authenticated user. */
export const canAccess = (role: AppRole | null, allow?: readonly AppRole[]): boolean =>
  allow === undefined ? true : role !== null && allow.includes(role)

/** Structural variant so heterogeneous route entries (some without `allow`) type-check. */
export const canAccessEntry = (
  role: AppRole | null,
  entry: { readonly allow?: readonly AppRole[]; readonly [key: string]: unknown }
): boolean => canAccess(role, entry.allow)
